// src/services/weatherService.js
// Primary weather data service — backed by TTN LoRaWAN API
// (api.wirelessplanet.co.ke/api/v1)

import { cacheService } from './cacheService';
import { AFRICLIMATE_API as TTN, apiHeaders as ttnHeaders, fetchWithTimeout } from './config';
import { mapReading } from './ttn/readings';
import { mapDevice } from './ttn/devices';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const determineDeviceStatus = (device, latestReading) => {
  const timestamp = latestReading?.timestamp || device?.lastSeen;
  if (!timestamp) return 'offline';
  try {
    const mins = (Date.now() - new Date(timestamp)) / 60000;
    if (mins <= 30)  return 'online';
    if (mins <= 120) return 'warning';
    return 'offline';
  } catch {
    return 'offline';
  }
};

const formatLastUpdate = (ts) => {
  if (!ts) return 'Unknown';
  try {
    const diff = Date.now() - new Date(ts);
    const mins = Math.floor(diff / 60000);
    const hrs  = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1)  return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    if (hrs  < 24) return `${hrs} hr ago`;
    return `${days} days ago`;
  } catch {
    return 'Unknown';
  }
};

/** Merge a mapped TTN device + its latest reading into the app's device shape */
const mergeDeviceAndReading = (device, reading) => {
  const status = determineDeviceStatus(device, reading);
  return {
    // Identity
    id:          device.id,
    name:        device.name || `Station ${device.id?.slice(-4)}`,
    type:        'Weather Station',
    deviceId:    device.id,
    stationId:   device.id,
    shortId:     device.id?.slice(-6),
    description: device.description || '',

    // Location
    lat:      device.lat,
    lng:      device.lng,
    altitude: device.altitude,
    location: device.location || '',  // human-readable string set by mapDevice (e.g. "Kimilili, Bungoma County, Kenya")

    // Status
    status,
    online:   status === 'online',
    isPublic: true,

    // Weather readings (null when no data)
    temperature:          reading?.temperature       ?? null,
    humidity:             reading?.humidity          ?? null,
    pressure:             reading?.pressure          ?? null,
    windSpeed:            reading?.wind_speed        ?? null,
    windDirection:        reading?.wind_dir          ?? null,
    rainfall:             reading?.rain_gauge        ?? null,
    rainfallAccumulation: reading?.rain_accum        ?? null,
    lightIntensity:       reading?.light             ?? null,
    uvIndex:              reading?.uv_index          ?? null,
    peakWindGust:         reading?.peak_gust         ?? null,

    // LoRaWAN signal
    rssi:        reading?.rssi        ?? null,
    snr:         reading?.snr         ?? null,
    gatewayId:   reading?.gatewayId   ?? null,
    allGateways: reading?.allGateways ?? [],
    linkQuality: reading?.linkQuality ?? null,

    // Timestamps
    lastUpdate: formatLastUpdate(reading?.timestamp || device.lastSeen),
    timestamp:  reading?.timestamp || device.lastSeen || null,
  };
};

// ─── Public API ──────────────────────────────────────────────────────────────

// In-flight deduplication — all concurrent callers share one fetch promise
let _inflight = null;

/**
 * Fetch all weather stations with their latest readings.
 * Uses the TTN API: GET /devices + GET /readings/latest
 */
export const fetchWeatherDevices = async (useCache = true) => {
  // Version-stamp the cache key so stale pre-fix data is never returned
  const CACHE_KEY = 'weather_devices_ttn_v2';

  if (useCache) {
    const cached = cacheService.get(CACHE_KEY);
    if (cached) {
      console.log(`⚡ CACHED: ${cached.length} stations (${cacheService.getAge(CACHE_KEY)}s old)`);
      return cached;
    }
  }

  // If another caller is already fetching, return the same promise
  if (_inflight) return _inflight;

  _inflight = (async () => {
  // Fetch devices and latest readings in parallel
  const [devicesRes, readingsRes] = await Promise.allSettled([
    fetchWithTimeout(fetch(`${TTN.BASE_URL}/devices`, { headers: ttnHeaders })),
    fetchWithTimeout(fetch(`${TTN.BASE_URL}/readings/latest`, { headers: ttnHeaders })),
  ]);

  // Process devices
  let devices = [];
  if (devicesRes.status === 'fulfilled' && devicesRes.value.ok) {
    const json = await devicesRes.value.json();
    devices = (json.data || []).map(mapDevice);
    console.log(`✅ Loaded ${devices.length} devices`);
  } else {
    console.warn('⚠️ Could not load devices:', devicesRes.reason || devicesRes.value?.status);
  }

  // Process latest readings — build a Map keyed by deviceId
  const readingsByDevice = new Map();
  if (readingsRes.status === 'fulfilled' && readingsRes.value.ok) {
    const json = await readingsRes.value.json();
    for (const r of (json.data || [])) {
      const mapped = mapReading(r);
      readingsByDevice.set(mapped.deviceId, mapped);
    }
    console.log(`✅ Loaded readings for ${readingsByDevice.size} devices`);
  } else {
    console.warn('⚠️ Could not load latest readings:', readingsRes.reason || readingsRes.value?.status);
  }

  // For any device still missing a reading, fetch its last known reading individually
  const missing = devices.filter(d => !readingsByDevice.has(d.id));
  if (missing.length > 0) {
    console.log(`🔍 Fetching individual readings for ${missing.length} devices...`);
    await Promise.allSettled(
      missing.map(async (d) => {
        try {
          const res = await fetchWithTimeout(
            fetch(`${TTN.BASE_URL}/readings/device/${d.id}?limit=1`, { headers: ttnHeaders })
          );
          if (!res.ok) return;
          const json = await res.json();
          const raw = json.data?.[0];
          if (raw) readingsByDevice.set(d.id, mapReading(raw));
        } catch (_) {}
      })
    );
    console.log(`✅ Filled in readings for ${missing.filter(d => readingsByDevice.has(d.id)).length} more devices`);
  }

  // Merge
  const merged = devices.map(d => mergeDeviceAndReading(d, readingsByDevice.get(d.id) ?? null));
  console.log(`✅ ${merged.length} stations ready (${merged.filter(s => s.temperature !== null).length} with sensor data)`);

  cacheService.set(CACHE_KEY, merged, 5 * 60 * 1000);
  return merged;
  })();
  _inflight.finally(() => { _inflight = null; });
  return _inflight;
};

/**
 * Fetch historical readings for a specific device / date range.
 * Uses: GET /readings with deviceId, startDate, endDate, page, limit params.
 */
export const fetchDetailedWeatherData = async ({
  stationId    = null,
  fromDateTime = null,
  toDateTime   = null,
  page         = 1,
  pageSize     = 100,
}) => {
  const p = new URLSearchParams({ page, limit: pageSize });
  if (stationId)    p.set('deviceId', stationId);
  if (fromDateTime) p.set('startDate', new Date(fromDateTime).toISOString());
  if (toDateTime)   p.set('endDate',   new Date(toDateTime).toISOString());

  const url = `${TTN.BASE_URL}/readings?${p}`;
  console.log('📈 Historical data:', url);

  const res = await fetchWithTimeout(fetch(url, { headers: ttnHeaders }));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const json = await res.json();
  const data = (json.data || []).map(mapReading);

  return {
    data,
    total:   json.total   ?? data.length,
    page:    json.page    ?? page,
    pageSize: json.limit  ?? pageSize,
    hasNext: json.hasNext ?? false,
    hasPrev: page > 1,
  };
};

/**
 * Fetch ALL historical data (paginated) for export.
 */
export const fetchAllDetailedData = async ({
  stationId    = null,
  fromDateTime = null,
  toDateTime   = null,
  onProgress   = null,
}) => {
  let allData = [];
  let page = 1;
  let hasNext = true;

  while (hasNext) {
    const result = await fetchDetailedWeatherData({ stationId, fromDateTime, toDateTime, page, pageSize: 100 });
    allData.push(...result.data);
    hasNext = result.hasNext;
    page++;

    if (onProgress) onProgress({ loaded: allData.length, total: result.total, page: page - 1 });
    console.log(`📥 ${allData.length} / ${result.total} records`);

    if (page > 100) { console.warn('⚠️ 100-page limit reached'); break; }
  }

  console.log(`✅ Export ready: ${allData.length} records`);
  return allData;
};

/**
 * Fetch the latest reading for a single station.
 */
export const fetchStationLatestData = async (stationId) => {
  try {
    const res = await fetchWithTimeout(
      fetch(`${TTN.BASE_URL}/readings/device/${stationId}?limit=1`, { headers: ttnHeaders })
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    const raw = json.data?.[0] || null;
    return raw ? mapReading(raw) : null;
  } catch (error) {
    console.error(`fetchStationLatestData(${stationId}):`, error);
    throw error;
  }
};

/** Simple retry wrapper */
export const fetchWithRetry = async (fetchFunction, maxRetries = 2) => {
  for (let i = 0; i < maxRetries; i++) {
    try { return await fetchFunction(); }
    catch (error) {
      console.log(`Attempt ${i + 1} failed: ${error.message}`);
      if (i === maxRetries - 1) throw error;
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
    }
  }
};

const weatherService = {
  fetchWeatherDevices,
  fetchDetailedWeatherData,
  fetchAllDetailedData,
  fetchStationLatestData,
  fetchWithRetry,
};

export { weatherService as default };
